import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  useCreateTaskSchedule,
  useScheduleExecutions,
  useTaskSchedule,
  useUpdateTaskSchedule,
} from "@/features/scheduler/hooks/useTaskSchedules";
import { RecurrenceEditor } from "@/features/scheduler/components/RecurrenceEditor";
import {
  payloadToPreset,
  presetToPayload,
  type RecurrencePreset,
} from "@/features/scheduler/lib/recurrence";
import {
  EMPTY_TASK_FORM,
  TaskForm,
  type TaskFormValue,
  taskFormToPayload,
  validateTaskForm,
} from "@/features/tasks/components/TaskForm";
import { TasksTable } from "@/features/tasks/components/TasksTable";

const DEFAULT_PRESET: RecurrencePreset = { kind: "daily", time: "09:00" };

export function ScheduleEditorPage() {
  const { scheduleId } = useParams();
  const isEdit = Boolean(scheduleId);
  const { t } = useTranslation("scheduler");
  const { t: tTasks } = useTranslation("tasks");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "history" ? "history" : "config";

  const scheduleQuery = useTaskSchedule(scheduleId ?? "");
  const create = useCreateTaskSchedule();
  const update = useUpdateTaskSchedule(scheduleId ?? "");

  const [form, setForm] = useState<TaskFormValue>(EMPTY_TASK_FORM);
  const [preset, setPreset] = useState<RecurrencePreset>(DEFAULT_PRESET);

  // Edit: hidrata o form quando o schedule chega.
  useEffect(() => {
    const s = scheduleQuery.data;
    if (!s) return;
    setForm({
      targetKind: s.agent_id ? "agent" : "team",
      agentId: s.agent_id ?? "",
      teamSessionId: s.team_session_id ?? "",
      title: s.title,
      prompt: s.prompt,
      maxTokens: s.max_tokens != null ? String(s.max_tokens) : "",
    });
    setPreset(payloadToPreset(s));
  }, [scheduleQuery.data]);

  async function handleSave() {
    const errorKey = validateTaskForm(form);
    if (errorKey) {
      toast.error(tTasks(errorKey));
      return;
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const recurrence = presetToPayload(preset);
    try {
      if (isEdit) {
        await update.mutateAsync({
          ...taskFormToPayload(form),
          ...recurrence,
          // PATCH parcial: o backend exige XOR -- envia o lado vazio
          // como null para trocar de alvo sem violar o CHECK.
          agent_id: form.targetKind === "agent" ? form.agentId : null,
          team_session_id:
            form.targetKind === "team" ? form.teamSessionId : null,
          max_tokens: form.maxTokens ? Number(form.maxTokens) : null,
          cron_expression: recurrence.cron_expression ?? null,
          run_at: recurrence.run_at ?? null,
          timezone,
        });
        toast.success(t("toasts.updated"));
      } else {
        await create.mutateAsync({
          ...taskFormToPayload(form),
          ...recurrence,
          timezone,
        });
        toast.success(t("toasts.created"));
        navigate("/scheduler");
      }
    } catch (err) {
      toastApiError(err);
    }
  }

  if (isEdit && scheduleQuery.isLoading) return <LoadingState />;
  if (isEdit && scheduleQuery.isError) {
    return (
      <ErrorState
        error={scheduleQuery.error}
        onRetry={() => void scheduleQuery.refetch()}
      />
    );
  }

  const configPanel = (
    <div className="max-w-2xl space-y-6">
      <TaskForm value={form} onChange={setForm} />
      <RecurrenceEditor value={preset} onChange={setPreset} />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => navigate("/scheduler")}>
          {t("actions.back")}
        </Button>
        <Button
          onClick={() => void handleSave()}
          disabled={create.isPending || update.isPending}
        >
          {t("actions.save")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/scheduler"
        title={isEdit ? (scheduleQuery.data?.title ?? "") : t("new.title")}
        description={isEdit ? t("edit.description") : t("new.description")}
      />

      {isEdit ? (
        <Tabs
          value={tab}
          onValueChange={(v) =>
            setSearchParams(v === "history" ? { tab: "history" } : {})
          }
        >
          <TabsList>
            <TabsTrigger value="config">{t("tabs.config")}</TabsTrigger>
            <TabsTrigger value="history">{t("tabs.history")}</TabsTrigger>
          </TabsList>
          <TabsContent value="config" className="pt-4">
            {configPanel}
          </TabsContent>
          <TabsContent value="history" className="pt-4">
            <HistoryTab scheduleId={scheduleId ?? ""} />
          </TabsContent>
        </Tabs>
      ) : (
        configPanel
      )}
    </div>
  );
}

function HistoryTab({ scheduleId }: { scheduleId: string }) {
  const { t } = useTranslation("scheduler");
  const { data, isLoading, isError, error, refetch } =
    useScheduleExecutions(scheduleId);

  if (isLoading) return <LoadingState />;
  if (isError) {
    return <ErrorState error={error} onRetry={() => void refetch()} />;
  }
  if (!data || data.length === 0) {
    return (
      <EmptyState
        title={t("history.empty.title")}
        description={t("history.empty.description")}
      />
    );
  }
  return <TasksTable tasks={data} hideTargetColumn />;
}
